import { useState, useEffect, useCallback } from 'react';
import { Filter, RefreshCw, X, Check } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface CohortRow {
  cohort_month: string;
  enrollments: number; first_time: number; repeats: number;
  gross: number; arpu: number; brand_new: number;
  avg_pre_ltv: number; avg_post_30d: number; avg_post_90d: number;
  l3_trial_pct: number; l3_paid_pct: number; any_membership_pct: number; upsell_pct: number;
}

interface StudentRow {
  purchase_date: string; email: string; amount: number; source: string;
  enrollment_seq: number; total_enrollments_for_person: number;
  ltv_pre_class: number; ltv_post_class_30d: number; ltv_post_class_90d: number;
  level3_trial_within_30d: boolean; level3_paid_within_30d: boolean; legacy_keap_member_within_30d: boolean;
}

const fmt = (v: number) => '$' + Math.round(Math.abs(Number(v) || 0)).toLocaleString();
const monthLabel = (d: string) => new Date(d + 'T00:00:00').toLocaleString('en', { month: 'short', year: '2-digit' });

const EXCLUDE_KEY = 'sp30Cohorts.excludeClubs';

export default function SP30Cohorts() {
  const [since, setSince] = useState('2025-11-01');
  const [until, setUntil] = useState('');
  const [excludeClubs, setExcludeClubs] = useState(() => localStorage.getItem(EXCLUDE_KEY) === '1');
  const [rows, setRows] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CohortRow | null>(null);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const toggleExclude = () => {
    setExcludeClubs(v => {
      try { localStorage.setItem(EXCLUDE_KEY, v ? '0' : '1'); } catch { /* private mode */ }
      return !v;
    });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/sp30-cohorts?since=${since}${until ? `&until=${until}` : ''}${excludeClubs ? '&exclude=clubs' : ''}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setRows(j.data || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [since, until, excludeClubs]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row: CohortRow) => {
    setSelected(row); setStudents([]); setDetailLoading(true);
    try {
      const month = String(row.cohort_month).slice(0, 10);
      const r = await fetch(`${API_BASE}/financial-reports/sp30-cohorts/detail?month=${month}${excludeClubs ? '&exclude=clubs' : ''}`);
      const j = await r.json();
      if (j.success) setStudents(j.data || []);
    } catch { /* empty state */ }
    setDetailLoading(false);
  };

  const tot = rows.reduce((a, r) => ({
    enrollments: a.enrollments + Number(r.enrollments),
    firstTime: a.firstTime + Number(r.first_time),
    repeats: a.repeats + Number(r.repeats),
    gross: a.gross + Number(r.gross),
    preLtvWeighted: a.preLtvWeighted + Number(r.avg_pre_ltv) * Number(r.enrollments),
    post90Weighted: a.post90Weighted + Number(r.avg_post_90d) * Number(r.enrollments),
    memberWeighted: a.memberWeighted + Number(r.any_membership_pct) * Number(r.enrollments),
  }), { enrollments: 0, firstTime: 0, repeats: 0, gross: 0, preLtvWeighted: 0, post90Weighted: 0, memberWeighted: 0 });
  const w = (v: number) => tot.enrollments > 0 ? v / tot.enrollments : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SP30 Cohorts</h1>
          <p className="text-sm text-gray-500">
            Single Plane 30 enrollments by month — who buys, what they spend before and after, and the membership bridge (Keap + Shopify, cross-source deduped).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={since} onChange={e => setSince(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <button
            onClick={toggleExclude}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              excludeClubs ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Filter className="h-4 w-4" /> {excludeClubs ? 'Excluding Clubs & Trade-Ins' : 'Exclude Clubs & Trade-Ins'}
          </button>
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      {excludeClubs && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm">
          <span className="font-semibold">Filtered view</span> — pre-class LTV and +30d/+90d spend exclude Custom Club and Trade-In lines.
          Enrollments, gross, and the membership bridge are unchanged (SP30 class sales never include clubs).
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {[
          ['Enrollments', tot.enrollments.toLocaleString()],
          ['Gross', fmt(tot.gross)],
          ['ARPU', fmt(w(tot.gross))],
          ['Repeat share', tot.enrollments ? Math.round(100 * tot.repeats / tot.enrollments) + '%' : '--'],
          ['Avg spend before class', fmt(w(tot.preLtvWeighted))],
          ['Any membership ≤30d', w(tot.memberWeighted).toFixed(1) + '%'],
        ].map(([label, v]) => (
          <div key={label as string} className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className="text-xl font-bold text-gray-900">{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Month', 'Enrollments', 'First / Repeat', 'Gross', 'ARPU', 'Pre-class LTV', '+30d spend', '+90d spend', 'L3 trial', 'L3 paid', 'Any member', 'Upsell ≤3d'].map(h => (
                <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <tr key={r.cohort_month} onClick={() => openDetail(r)} className="cursor-pointer hover:bg-blue-50">
                <td className="px-3 py-2 font-medium">{monthLabel(String(r.cohort_month).slice(0, 10))}</td>
                <td className="px-3 py-2 text-right font-semibold">{r.enrollments}</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.first_time} / {r.repeats}</td>
                <td className="px-3 py-2 text-right">{fmt(r.gross)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(r.arpu)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(r.avg_pre_ltv)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.avg_post_30d)}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmt(r.avg_post_90d)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.l3_trial_pct}%</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.l3_paid_pct}%</td>
                <td className="px-3 py-2 text-right font-semibold text-violet-700">{r.any_membership_pct}%</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.upsell_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{monthLabel(String(selected.cohort_month).slice(0, 10))} — {selected.enrollments} enrollments</h2>
                <p className="text-sm text-gray-500">Per-student pre-class spend, post-class spend, and membership conversion</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto p-4">
              {detailLoading ? (
                <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
              ) : students.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">No enrollments found.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b">
                      <th className="text-left py-2 pr-2">Student</th>
                      <th className="text-right py-2 px-2">Paid</th>
                      <th className="text-right py-2 px-2">Take #</th>
                      <th className="text-right py-2 px-2">Pre-class LTV</th>
                      <th className="text-right py-2 px-2">+30d</th>
                      <th className="text-right py-2 px-2">+90d</th>
                      <th className="text-center py-2 pl-2">Member ≤30d</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-1.5 pr-2 truncate max-w-[220px]">{s.email || <span className="text-gray-400">(no email on record)</span>}</td>
                        <td className="text-right py-1.5 px-2">{fmt(s.amount)}</td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{s.enrollment_seq} of {s.total_enrollments_for_person}</td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{fmt(s.ltv_pre_class)}</td>
                        <td className="text-right py-1.5 px-2">{fmt(s.ltv_post_class_30d)}</td>
                        <td className="text-right py-1.5 px-2 font-medium">{fmt(s.ltv_post_class_90d)}</td>
                        <td className="text-center py-1.5 pl-2">
                          {(s.level3_trial_within_30d || s.level3_paid_within_30d || s.legacy_keap_member_within_30d)
                            ? <Check className="h-4 w-4 text-emerald-600 inline" /> : <span className="text-gray-300">--</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Click any month for the student list. Pre-class LTV = lifetime spend before first SP30 purchase; +30d/+90d = additional spend after the class.
        Recent months' 30/90-day windows are still open, so their post-class numbers will keep growing. Source: wicked SP30 cohort views (same data as the July 2026 one-pager).
      </p>
    </div>
  );
}
